<template>
    <div class="card card-body shadow-sm mt-0">
      <div class="form-header d-flex justify-content-between align-items-center">
        <h4 class="form-title">
          {{ route.params.id ? 'Editar Meio de Pagamento' : 'Novo Meio de Pagamento' }}
        </h4>
        <button class="btn btn-secondary btn-sm me-3" @click="voltar">Voltar</button>
      </div>
  
      <BaseForm :isFormValid="isFormValid" :isSubmitting="isSubmitting" @submit="salvarMeio" @cancel="voltar">
        <!-- Dados do Meio de Pagamento -->
        <FormSection title="Informações do Meio">
          <!-- Container Flex para manter os dois campos na mesma linha -->
          <div class="d-flex gap-3 mb-3">
            <!-- Campo Operador com SelectField -->
            <div class="flex-fill larger-field">
              <SelectField
                id="operador_id"
                v-model="meio.operador_id"
                :options="operadoresOptions"
                placeholder="Selecione o operador"
                label="Operador <span class='required'>*</span>"
                required
              />
              <div v-if="errors.operador_id" class="error-message">{{ errors.operador_id }}</div>
            </div>
            <!-- Campo Nome do Meio com InputField -->
            <div class="flex-fill larger-field">
              <InputField
                id="meio"
                v-model="meio.meio"
                placeholder="Digite o nome do meio"
                label="Meio de Pagamento <span class='required'>*</span>"
                required
                :class="{ 'error-input': errors.meio }"
              />
              <div v-if="errors.meio" class="error-message">{{ errors.meio }}</div>
            </div>
          </div>
        </FormSection>
      </BaseForm>
    </div>
  </template>
  
  <script>
  import { reactive, ref, computed, onMounted } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import InputField from "@/components/Layout/InputField.vue";
  import SelectField from "@/components/Layout/SelectField.vue";
  import FormSection from "@/components/Layout/FormSection.vue";
  import BaseForm from "@/components/Layout/BaseForm.vue";
  import axios from "@/services/axios";
  
  export default {
    components: { InputField, SelectField, FormSection, BaseForm },
    setup() {
      const route = useRoute();
      const router = useRouter();
  
      const meio = reactive({
        id: null,
        operador_id: '',
        meio: "",
      });
      const errors = reactive({});
      const formTouched = ref(false);
      const isSubmitting = ref(false);
  
      // Para alimentar o SelectField, vamos criar uma propriedade que transforma os operadores em opções
      const operadores = ref([]);
      const operadoresOptions = computed(() => {
        return operadores.value.map(op => ({ text: op.operador, value: op.id }));
      });
  
      const fetchOperadores = async () => {
        try {
          const response = await axios.get('/financeiro/parametros/operadores');
          // Supondo que a resposta esteja no formato { data: [...] }
          operadores.value = response.data.data;
        } catch (error) {
          console.error("Erro ao carregar operadores:", error);
        }
      };
  
      const validateForm = () => {
        errors.operador_id = meio.operador_id ? "" : "Operador é obrigatório.";
        errors.meio = meio.meio ? "" : "O nome do meio é obrigatório.";
      };
  
      const isFormValid = computed(() => {
        return meio.operador_id && meio.meio.trim() !== "" && !Object.values(errors).some(error => error);
      });
  
      const novoMeio = () => {
        formTouched.value = false;
        isSubmitting.value = false;
        Object.assign(meio, {
          operador_id: '',
          meio: "",
        });
        for (const key in errors) {
          if (Object.hasOwn(errors, key)) errors[key] = "";
        }
      };
  
      const salvarMeio = async () => {
        if (isSubmitting.value) return;
        validateForm();
        if (!isFormValid.value) {
          console.error("O formulário contém erros:", errors);
          return;
        }
        isSubmitting.value = true;
        try {
          const method = meio.id ? "put" : "post";
          const url = meio.id
            ? `/financeiro/parametros/meios/${meio.id}`
            : "/financeiro/parametros/meios";
          await axios[method](url, {
            operador_id: meio.operador_id,
            meio: meio.meio,
          });
          router.push("/financeiro/parametros/meios");
        } catch (error) {
          console.error("Erro ao salvar meio:", error);
        } finally {
          isSubmitting.value = false;
        }
      };
  
      const voltar = () => {
        router.push("/financeiro/parametros/meios");
      };
  
      onMounted(async () => {
        await fetchOperadores();
        const meioId = route.params.id;
        if (meioId) {
          try {
            const response = await axios.get(`/financeiro/parametros/meios/${meioId}`);
            Object.assign(meio, response.data);
          } catch (error) {
            console.error("Erro ao carregar meio:", error);
          }
        } else {
          novoMeio();
        }
      });
  
      return {
        meio,
        errors,
        isFormValid,
        formTouched,
        isSubmitting,
        salvarMeio,
        voltar,
        route,
        operadoresOptions,
      };
    },
  };
  </script>
  
  <style scoped>
  .card {
    max-width: 90%;
    margin: 0 auto;
  }
  .error-input {
    border-color: #dc3545;
  }
  .error-message {
    color: #dc3545;
    font-size: 0.875rem;
  }
  .form-switch {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  /* Classe para aumentar a largura mínima dos campos */
  .larger-field {
    min-width: 250px; /* Ajuste esse valor conforme necessário */
  }
  </style>
  